import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { SITE_CONFIG } from '../config/siteConfig';
import { supabase } from '../lib/supabase';
import ProgressiveImage from './ProgressiveImage';

import { bannerService } from '../services/BannerService';
import { HeroBanner } from '../types';

const HeroCarousel = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [banners, setBanners] = useState<HeroBanner[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fallbackBanners: HeroBanner[] = SITE_CONFIG.heroBanners.map(b => ({
        id: String(b.id),
        title: b.title,
        subtitle: b.subtitle,
        image: b.image,
        position: b.position,
        active: true,
        order: b.id
    }));

    // Minimum swipe distance (in px) to trigger slide change
    const minSwipeDistance = 50;

    useEffect(() => {
        const loadBanners = async () => {
            const dbBanners = await bannerService.getAll();
            if (dbBanners.length > 0) {
                setBanners(dbBanners);
            } else {
                setBanners(fallbackBanners);
            }
            setIsLoading(false);
        };

        loadBanners();

        // Realtime Subscription
        const channel = supabase
            .channel('hero_banners_changes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'hero_banners'
                },
                (payload) => {
                    console.log('Realtime banner update:', payload);
                    loadBanners();
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    useEffect(() => {
        if (!isPaused && banners.length > 0) {
            const timer = setInterval(() => {
                setCurrentIndex((prev) => (prev + 1) % banners.length);
            }, 5000);
            return () => clearInterval(timer);
        }
    }, [isPaused, banners.length]);

    if (isLoading) return <div className="h-[300px] bg-zinc-950 animate-pulse" />;
    if (banners.length === 0) return null;

    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
    };

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null); // Reset touchEnd
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleNext();
        } else if (isRightSwipe) {
            handlePrevious();
        }
    };

    return (
        <section
            id="hero-carousel-section"
            className="relative w-full h-[300px] md:h-[450px] lg:h-[550px] overflow-hidden bg-zinc-950 group"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7 }}
                    className="absolute inset-0"
                >
                    {/* Background Media (Image or Video) */}
                    {banners[currentIndex].image?.match(/\.(mp4|webm|ogg)$/i) ? (
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{
                                objectPosition: banners[currentIndex].position || 'center',
                                transform: isPaused
                                    ? `scale(${banners[currentIndex].scale || 1.0})`
                                    : `scale(${(banners[currentIndex].scale || 1.0) + 0.05})`,
                                transformOrigin: banners[currentIndex].position || 'center',
                                transition: 'transform 5s linear'
                            }}
                        >
                            <source src={banners[currentIndex].image} type="video/mp4" />
                        </video>
                    ) : (
                        <ProgressiveImage
                            src={banners[currentIndex].image}
                            alt={banners[currentIndex].title || 'Banner'}
                            fill={true}
                            className="transition-transform duration-[5000ms] ease-linear"
                            style={{
                                objectPosition: banners[currentIndex].position || 'center',
                                transform: isPaused
                                    ? `scale(${banners[currentIndex].scale || 1.0})`
                                    : `scale(${(banners[currentIndex].scale || 1.0) + 0.05})`,
                                transformOrigin: banners[currentIndex].position || 'center'
                            }}
                            loading="eager"
                        />
                    )}

                    {/* Gradient Overlay for Text Readability & Smooth Blend */}
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />

                    {/* Content */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8">
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.3, duration: 0.5 }}
                                className="max-w-2xl"
                            >
                                {banners[currentIndex].title && (
                                    <h2 className="text-3xl sm:text-4xl md:text-6xl font-display font-black text-white italic uppercase tracking-tighter mb-2 drop-shadow-lg">
                                        {banners[currentIndex].title}
                                    </h2>
                                )}
                                {banners[currentIndex].subtitle && (
                                    <p className="text-sm sm:text-base md:text-xl text-gray-200 font-medium drop-shadow-md border-l-4 border-white pl-3 md:pl-4 mb-4 md:mb-6">
                                        {banners[currentIndex].subtitle}
                                    </p>
                                )}
                                <a
                                    href="#catalog-section"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const catalog = document.getElementById('catalog-section');
                                        if (catalog) {
                                            catalog.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }}
                                    className="inline-flex items-center gap-2 bg-white hover:bg-zinc-200 text-black font-display font-black uppercase text-xs md:text-sm tracking-widest py-2.5 px-5 md:py-3.5 md:px-7 rounded-sm shadow-[0_0_25px_rgba(255,255,255,0.3)] transition-all transform hover:scale-[1.02] active:scale-[0.98] no-underline"
                                >
                                    <span>Explorar Catálogo</span>
                                    <ArrowRight className="w-4 h-4" />
                                </a>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons - Visible on Hover */}
            <button
                id="btn-hero-prev"
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-white text-white hover:text-black p-2 rounded-full backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 hidden md:block"
                aria-label="Anterior"
            >
                <ChevronLeft className="w-8 h-8" />
            </button>
            <button
                id="btn-hero-next"
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-white text-white hover:text-black p-2 rounded-full backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 hidden md:block"
                aria-label="Siguiente"
            >
                <ChevronRight className="w-8 h-8" />
            </button>

            {/* Pagination Dots */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                {banners.map((_, idx) => (
                    <button
                        key={idx}
                        id={`btn-hero-dot-${idx}`}
                        onClick={() => setCurrentIndex(idx)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${idx === currentIndex
                            ? 'bg-white w-8'
                            : 'bg-white/50 hover:bg-white'
                            }`}
                        aria-label={`Ir a diapositiva ${idx + 1}`}
                    />
                ))}
            </div>

            {/* Bottom Fade to blend with next section */}
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-zinc-950 to-transparent pointer-events-none" />
        </section>
    );
};

export default HeroCarousel;
